import Link from '@components/common/Link/Link'
import SublimeMedia from '@components/common/SublimeMedia/SublimeMedia'

import clsx from 'clsx'
import gsap from 'gsap'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import 'swiper/css'
import { Autoplay, Keyboard, Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import SwiperType from 'swiper/types/swiper-class'
import s from './HeroSlider.module.scss'

import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import { FeaturedSliderContentFragment } from '@lib/umbraco/generated/types.generated'
import Flip from 'gsap/dist/Flip'
import { useInView } from 'react-intersection-observer'
import { AutoplayOptions } from 'swiper/types/modules/autoplay'

export type HeroSliderProps = {
  sliderContent: FeaturedSliderContentFragment[]
  animated?: boolean
  backgroundColor?: string
}

const HeroSlider: React.FC<HeroSliderProps> = ({
  sliderContent,
  animated,
  backgroundColor,
}) => {
  const { ref, inView, entry } = useInView({
    /* Optional options */
    threshold: 0.5,
    rootMargin: '0px',
  })

  gsap.registerPlugin(Flip)
  /* 
    Refs and states 
  */
  const swiper = useRef<SwiperType | null>(null)
  const [index, setIndex] = useState<number | null>(0)
  const [autoPlayOptions, setAutoPlayOptions] = useState<
    AutoplayOptions | undefined
  >(undefined)
  const [inactiveDefault, setInactiveDefault] = useState<boolean>(true)

  /* 
      Effects 
    */

  useEffect(() => {
    if (
      /* 
          if 
            a) there is no animation props
            b) SliceOut animation is selected and there is less than 4 elements (then it can't be played).
  
          then don't play animation, and reset to the defaults.
        */
      !animated ||
      sliderContent.length < 4
    ) {
      setInactiveDefault(false)
      setAutoPlayOptions({
        delay: 5000,
        disableOnInteraction: true,
        pauseOnMouseEnter: true,
      })

      return
    }

    if (animated) {
      requestAnimationFrame(() => {
        setInactiveDefault(false)
        createSliceOutAnimation()
      })
    }
  }, [])

  // Side effect for the "SliceOut" animation, to ensure it first plays when the user sees the slider.
  // Typically the slider will be placed at the bottom of a page when using SliceOut.
  useEffect(() => {
    if (!animated || !inView) return
    setTimeout(() => {
      playTransformOut().then(() => {
        setAutoPlayOptions({
          delay: 5000,
          disableOnInteraction: true,
          pauseOnMouseEnter: true,
        })
      })
    }, 200)
  }, [inView])

  // Side effect for following the cursor
  useEffect(() => {
    const followArrowCursor = (e: MouseEvent) => {
      const cursor = document.querySelector(
        `.${s.sliderWrapper}`,
      ) as HTMLDivElement
      if (cursor) {
        cursor.setAttribute(
          'dir',
          e.clientX > screen.width / 2 ? 'right' : 'left',
        )
      }
    }

    document.addEventListener('mousemove', followArrowCursor)
    return () => document.removeEventListener('mousemove', followArrowCursor)
  }, [])

  // Side effect for clicking on the slider
  useEffect(() => {
    const handleSliderClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target) return
      if (!target.classList.contains(s.mousearea)) return

      const cursor = document.querySelector(
        `.${s.sliderWrapper}`,
      ) as HTMLDivElement

      if (cursor.getAttribute('dir') === 'right') {
        swiper.current?.slideNext()
      } else {
        swiper.current?.slidePrev()
      }
    }

    const sliderWrapper = document.querySelector(
      `.${s.sliderWrapper}`,
    ) as HTMLDivElement
    if (!sliderWrapper) return

    sliderWrapper.addEventListener('click', handleSliderClick)
    return () => sliderWrapper.removeEventListener('click', handleSliderClick)
  }, [])

  // On
  const [height, setHeight] = useState<number | undefined>(undefined)
  useEffect(() => {
    if (inactiveDefault) return
    const slideItem = document.querySelector(`.${s.slideItem}`)
    if (!slideItem) return
    const slideItemHeight = slideItem.getBoundingClientRect().height
    setHeight(slideItemHeight)
  }, [inactiveDefault])

  const popdownTimeout = useRef<NodeJS.Timeout | null>(null)

  return (
    <>
      <div
        className={clsx(s.sliderWrapper, {
          [s.inactive]: inactiveDefault,
          [s.spawned]: !inactiveDefault,
        })}
        ref={ref}
        style={
          {
            '--slider-bg-color': backgroundColor ?? 'transparent',
            '--slide-height': `${height}px`,
          } as CSSProperties
        }
      >
        <Swiper
          draggable={false}
          simulateTouch={false}
          id="swiper-main"
          spaceBetween={1}
          loop={sliderContent.length > 2}
          slidesPerView={'auto'}
          slidesPerGroup={1}
          // cssMode
          breakpoints={{
            0: {
              slidesPerView: 1.5,
            },
            1024: {
              slidesPerView: 3.5,
              spaceBetween: 50,
              slidesPerGroup: 1,
            },
          }}
          onSlideChange={() => {
            if (popdownTimeout.current) clearTimeout(popdownTimeout.current)

            popdownTimeout.current = setTimeout(() => {
              setIndex(swiper.current?.realIndex ?? null)
              const item = sliderContent.find((_, itemIndex) => {
                return itemIndex == swiper.current?.realIndex
              })
            }, 200)
          }}
          preventInteractionOnTransition={true}
          longSwipes={true}
          onSwiper={(currentSwiper: SwiperType) => {
            swiper.current = currentSwiper
            setIndex(currentSwiper.realIndex)
          }}
          modules={[Keyboard, Mousewheel, Autoplay]}
          autoplay={autoPlayOptions}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          mousewheel={{
            thresholdDelta: 20,
            forceToAxis: true,
          }}
          centeredSlides
          className={s.slider}
        >
          {sliderContent.map((item, i) => {
            return (
              <SwiperSlide className={s.slide} key={i}>
                <div className={s.mousearea}></div>
                {item.contentLink ? (
                  <Link
                    link={item.contentLink}
                    onClick={(e) => {
                      if (animated) {
                        e.preventDefault()
                        window.location.href = item.contentLink.url ?? ''

                        return
                      }
                    }}
                  >
                    <div
                      className={clsx(s.slideItem, `${s.slideItem}-${i}`, {
                        [s.active]: index != null && index == i,
                      })}
                    >
                      <div className={s.slideImageContainer}>
                        <div className={s.slideImageFiller}></div>
                        <div className={s.slideImage}>
                          <SublimeMedia
                            className={s.nextImage}
                            mediaPicker={{
                              ...item.image,
                              media: {
                                ...item.image.media,
                                //@ts-ignore
                                shopTheLookJson: '',
                              },
                            }}
                          />
                        </div>
                      </div>
                      <div className={s.slideFooter}>
                        <div className={s.footerContent}>
                          <p className={s.footerHero}>{item.teaserText}</p>
                          <p className={s.footerTitle}>{item.mainText}</p>
                          <TrustedHtml
                            className={s.footerSubtitle}
                            html={item.descriptionText}
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div
                    className={clsx(
                      s.slideItem,
                      s.unsetCursor,
                      `${s.slideItem}-${i}`,
                      {
                        [s.active]: index != null && index == i,
                      },
                    )}
                  >
                    <div className={s.slideImageContainer}>
                      <div className={s.slideImageFiller}></div>
                      <div className={s.slideImage}>
                        <SublimeMedia
                          className={s.nextImage}
                          mediaPicker={{
                            ...item.image,
                            media: {
                              ...item.image.media,
                              //@ts-ignore
                              shopTheLookJson: '',
                            },
                          }}
                        />
                      </div>
                    </div>
                    <div className={s.slideFooter}>
                      <div className={s.footerContent}>
                        <p className={s.footerHero}>{item.teaserText}</p>
                        <p className={s.footerTitle}>{item.mainText}</p>
                        <TrustedHtml
                          className={s.footerSubtitle}
                          html={item.descriptionText}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
      <div className={s.kristianCursor}></div>
    </>
  )
}

export default HeroSlider

/* 
    Animation helper functions 
  */

function playTransformOut() {
  return new Promise((resolve, _) => {
    const slidesInView = Array.from(
      document.querySelectorAll(`.${s.slideItem}`),
    ).filter((slide) => {
      const rect = slide.getBoundingClientRect()
      return (
        rect.left + rect.width >= 0 &&
        rect.right - rect.width <=
          (window.innerWidth || document.documentElement.clientWidth)
      )
    })

    const states = Flip.getState([...slidesInView])
    slidesInView.forEach((slide) =>
      gsap.set(slide, {
        clearProps: 'transform',
        left: 0,
        position: 'relative',
      }),
    )
    Flip.from(states, {
      targets: slidesInView,
      duration: 0.5,
    }).then(() => resolve({}))
  })
}

function createSliceOutAnimation() {
  const slidesInView = Array.from(
    document.querySelectorAll(`.${s.slideItem}`),
  ).filter((slide) => {
    const rect = slide.getBoundingClientRect()
    return (
      rect.left + rect.width >= 0 &&
      rect.right - rect.width <=
        (window.innerWidth || document.documentElement.clientWidth)
    )
  })

  // Find active slide
  const activeSlide = slidesInView.find((slide) => {
    return slide.classList.contains(s.active)
  })
  if (!activeSlide) return

  // Flip all other slides to fit the active slide
  slidesInView.forEach((slide, slideIndex) => {
    if (slide === activeSlide) return
    Flip.fit(slide, activeSlide, { duration: 0 })
    requestAnimationFrame(() => {
      gsap.set(slide, {
        // rotationZ: (slideIndex < slidesInView.length / 2 ? -1 : 1) * 30,
        left:
          ((slideIndex < slidesInView.length / 2 ? -1 : 1) *
            slide.clientWidth) /
          4,
        position: 'absolute',
      })
    })
  })
}
